import validUrl from 'valid-url';
import { PHTOT_TYPE_SYGIC } from './constants';
import { browserCanUseWebp } from './checkBrowser';
import { getTripCoverUrlByNumber } from '../utilities/trip_cover_util';

const CryptoJS = require('crypto-js');

const HMAC_SECRET = 'poibank_thumbor_key';
const THUMBNAIL_IMAGE_WIDTH = 400;
const DEFAULT_IMG = require('../images/common/img_venu_default.png');

const getPinIconUrlByDay = day => {
  const value = parseInt(day, 10) % 10;
  switch (value) {
    case 1:
      return 'https://firebasestorage.googleapis.com/v0/b/ethereal-tract-801.appspot.com/o/pins%2Ficon_venu_pin_1.png?alt=media';
    case 2:
      return 'https://firebasestorage.googleapis.com/v0/b/ethereal-tract-801.appspot.com/o/pins%2Ficon_venu_pin_2.png?alt=media';
    case 3:
      return 'https://firebasestorage.googleapis.com/v0/b/ethereal-tract-801.appspot.com/o/pins%2Ficon_venu_pin_3.png?alt=media';
    case 4:
      return 'https://firebasestorage.googleapis.com/v0/b/ethereal-tract-801.appspot.com/o/pins%2Ficon_venu_pin_4.png?alt=media';
    case 5:
      return 'https://firebasestorage.googleapis.com/v0/b/ethereal-tract-801.appspot.com/o/pins%2Ficon_venu_pin_5.png?alt=media';
    case 6:
      return 'https://firebasestorage.googleapis.com/v0/b/ethereal-tract-801.appspot.com/o/pins%2Ficon_venu_pin_6.png?alt=media';
    case 7:
      return 'https://firebasestorage.googleapis.com/v0/b/ethereal-tract-801.appspot.com/o/pins%2Ficon_venu_pin_7.png?alt=media';
    case 8:
      return 'https://firebasestorage.googleapis.com/v0/b/ethereal-tract-801.appspot.com/o/pins%2Ficon_venu_pin_8.png?alt=media';
    case 9:
      return 'https://firebasestorage.googleapis.com/v0/b/ethereal-tract-801.appspot.com/o/pins%2Ficon_venu_pin_9.png?alt=media';
    case 0:
      return 'https://firebasestorage.googleapis.com/v0/b/ethereal-tract-801.appspot.com/o/pins%2Ficon_venu_pin_10.png?alt=media';
    default:
      return 'https://firebasestorage.googleapis.com/v0/b/ethereal-tract-801.appspot.com/o/pins%2Ficon_venu_pin_1.png?alt=media';
  }
};

const getUrlByReference = (reference, width, height, key) => {
  if (!reference) {
    return '';
  }
  if (validUrl.isUri(reference)) {
    return reference.replace('nxn', `${width}x${height}`);
  }
  return `https://maps.googleapis.com/maps/api/place/photo?photoreference=${reference}&key=${key}&sensor=true&maxwidth=${width}&maxHeight=${height}`;
};

const getFoursquareImageUrlBySize = (url, width, height) => {
  if (!url) {
    return '';
  }
  return url.replace('/nxn/', `/${width}x${height}/`);
};

const getFacebookProfileImageUrl = url => {
  // https://graph.facebook.com/10215638005493918/picture?type=large
  // https://graph.facebook.com/10215638005493918/picture?type=square&height=100
  if (url.indexOf('?') > -1) {
    return `${url.substring(0, url.indexOf('?'))}?type=square&height=100`;
  }
  return url;
};

// original image url from poi bank photo object
const getPoiBankSSRImageUrl = (object, userAgent) => {
  if (!object) {
    return null;
  }
  const { host, file, width } = object;

  if (!file || !host) {
    return null;
  }

  // poi bank
  const encode = ssrImgEncode(file, width, userAgent);

  return `${host}${encode.signature}/${encode.base64}`;
};

// image url from poi bank photo object
const getPoiBankImageUrl = (object, width, useJpg = false) => {
  if (!object) {
    return null;
  }
  const { host, file, type } = object;

  if (!file || !host) {
    return null;
  }

  // sygic
  if (type && type === PHTOT_TYPE_SYGIC) {
    return host + file;
  }

  // poi bank
  const encode = imgEncode(file, width, useJpg);

  return `${host}${encode.signature}/${encode.base64}`;
};

/** 處理舊版 img cover 格式(新版請用checkImgType)
 * @param {object} cover (required)舊版 cover object
 * @param {number} width (optional)欲取得圖片的寬度
 * @returns {string} Url 圖片網址
 */
const handleImgCoverOldFormated = (cover, width) => {
  if (cover.journal) return getPoiBankImageUrl(cover.journal, width);
  if (cover.trip) return cover.trip;
  if (cover.default) return getTripCoverUrlByNumber(cover.default);
  return DEFAULT_IMG;
};

// decode thumbnail image url from funliday server
const decodeThumbnailImageUrl = (fileData, width) => {
  if (!fileData || !width) {
    return null;
  }
  /*
    todo: 按過去此檔案 code 來看旅遊群組的話 fileData 應該是 Array ? 應該
    目前旅遊群組收藏只有 ios 有也不好測 先 return null 待日後更新
  */
  if (Array.isArray(fileData)) {
    return null;
  }
  const { file, host } = fileData;
  const encodeWith = width || THUMBNAIL_IMAGE_WIDTH;
  const encode = imgEncode(file, encodeWith);
  return `${host}${encode.signature}/${encode.base64}`;
};

const imgEncode = (fileName, width, useJpg = false) => {
  let canUseWebp = false;
  if (useJpg === false) {
    canUseWebp = browserCanUseWebp();
  }

  const dataBase64 = btoa(
    JSON.stringify([
      {
        format: canUseWebp ? 'webp' : 'jpg',
      },
      { key: fileName },
      {
        resize: {
          width: width.toString(),
        },
      },
    ]),
  );
  const signature = CryptoJS.HmacSHA1(dataBase64, HMAC_SECRET);
  const signatureBase64 = CryptoJS.enc.Base64.stringify(signature)
    .replace(/\//g, '_')
    .replace(/\+/g, '-');

  return {
    signature: signatureBase64,
    base64: dataBase64,
  };
};

const ssrImgEncode = (fileName, width) => {
  const dataBase64 = Buffer.from(
    JSON.stringify([
      {
        format: 'jpg',
      },
      { key: fileName },
      {
        resize: {
          width: width.toString(),
        },
      },
    ]),
  ).toString('base64');

  const signature = CryptoJS.HmacSHA1(dataBase64, HMAC_SECRET);
  const signatureBase64 = CryptoJS.enc.Base64.stringify(signature)
    .replace(/\//g, '_')
    .replace(/\+/g, '-');

  return {
    signature: signatureBase64,
    base64: dataBase64,
  };
};

// checkImgType 按照 img type 來取得圖片網址
const checkImgType = ({ data, isSSR = false, widthLimit, isJpg = false }) => {
  try {
    if (data && data.type === 1) {
      const { file } = data;
      return file || DEFAULT_IMG;
    }
    if (data && data.type === 4) {
      const { file } = data;
      return getTripCoverUrlByNumber(file) || DEFAULT_IMG;
    }
    if (data && isSSR && data.type === 5) {
      const { width } = data;
      const imgWidth = widthLimit || width;
      return getPoiBankSSRImageUrl(data, imgWidth) || DEFAULT_IMG;
    }
    if (data && isJpg && data.type === 5) {
      const { width } = data;
      const imgWidth = widthLimit || width;
      return getPoiBankImageUrl(data, imgWidth, true) || DEFAULT_IMG;
    }
    if (data && data.type === 5) {
      const { width } = data;
      const imgWidth = widthLimit || width;
      return getPoiBankImageUrl(data, imgWidth) || DEFAULT_IMG;
    }
    return DEFAULT_IMG;
  } catch (error) {
    console.error('checkImgType -> error', error);
    return DEFAULT_IMG;
  }
};

const getImageId = (filePath, isCover = false) => {
  if (!filePath) {
    return null;
  }

  const rule = isCover ? /.*\/cover\/(.*)+/g : /.*\/.*\/(.*)+/g;
  const matchAry = rule.exec(filePath);
  let imageId = null;

  if (matchAry) {
    imageId = matchAry[1];
  }

  return imageId;
};

export {
  getUrlByReference,
  getPinIconUrlByDay,
  getFacebookProfileImageUrl,
  getFoursquareImageUrlBySize,
  getPoiBankSSRImageUrl,
  handleImgCoverOldFormated,
  getPoiBankImageUrl,
  decodeThumbnailImageUrl,
  checkImgType,
  getImageId,
};
